import type {
    CommerceEventUnsubscribe,
    CommerceOperationOptions,
    CommerceShareAcceptanceStrategy,
    CommerceShareChannel,
    CommerceShareDomain,
    CommerceShareEvent,
    CommerceShareListener,
    CommerceSharePreview,
    CommerceShareProvider,
    CommerceShareResource,
    CommerceCart,
    CommerceShareTarget,
    CommerceStorefrontExperience,
    AcceptCommerceShareResponse,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceShareRepository } from "./commerce-share-repository.js";

export class CommerceShareService implements CommerceShareDomain {
    private readonly providerMap = new Map<CommerceShareChannel, CommerceShareProvider>();
    private readonly listeners = new DisposableListenerRegistry<CommerceShareEvent>();

    public constructor(
        private readonly repository: CommerceShareRepository,
        private readonly getStorefront: () => CommerceStorefrontExperience,
        private readonly getCartId: () => string | null,
        private readonly assertAvailable: () => void,
        private readonly acceptShare: (
            response: AcceptCommerceShareResponse,
        ) => Promise<CommerceCart>,
    ) {
        for (const provider of createDefaultProviders())
            this.providerMap.set(provider.channel, provider);
    }

    public get providers(): readonly CommerceShareProvider[] {
        return [...this.providerMap.values()]
            .filter((provider) => provider.isAvailable?.() ?? true)
            .sort((left, right) => (left.priority ?? 100) - (right.priority ?? 100));
    }

    public async create(
        target: CommerceShareTarget,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceShareResource> {
        this.assertAvailable();
        const storefront = this.getStorefront();
        const attributedTarget: CommerceShareTarget = {
            ...target,
            metadata: {
                ...(target.metadata ?? {}),
                ...(storefront.attribution.webAlias
                    ? { webAlias: storefront.attribution.webAlias }
                    : {}),
                ...(storefront.attribution.referrerId
                    ? { referrerId: storefront.attribution.referrerId }
                    : {}),
                ...(storefront.attribution.campaign
                    ? { campaign: storefront.attribution.campaign }
                    : {}),
            },
        };
        const resource = Object.freeze(await this.repository.create(attributedTarget, options));
        this.emit("created", resource);
        return resource;
    }

    public storefront(options?: CommerceOperationOptions): Promise<CommerceShareResource> {
        return this.create(
            {
                type: "storefront",
                title: this.getStorefront().contact?.displayName ?? "Storefront",
            },
            options,
        );
    }

    public product(
        productId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource> {
        return this.create({ type: "product", id: productId }, options);
    }
    public cart(options?: CommerceOperationOptions): Promise<CommerceShareResource> {
        const id = this.getCartId();
        return this.create({ type: "cart", ...(id ? { id } : {}) }, options);
    }
    public collection(
        collectionId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource> {
        return this.create({ type: "collection", id: collectionId }, options);
    }
    public routine(
        routineId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource> {
        return this.create({ type: "routine", id: routineId }, options);
    }
    public campaign(
        campaignId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource> {
        return this.create({ type: "campaign", id: campaignId }, options);
    }
    public coupon(
        couponCode: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource> {
        return this.create({ type: "coupon", id: couponCode }, options);
    }
    public page(
        url: string,
        title?: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource> {
        return this.create({ type: "page", url, ...(title ? { title } : {}) }, options);
    }

    public contact(options?: CommerceOperationOptions): Promise<CommerceShareResource> {
        const contact = this.getStorefront().contact;
        return this.create(
            {
                type: "contact",
                ...(contact ? { id: contact.id, title: contact.displayName } : {}),
            },
            options,
        );
    }

    public registerProvider(provider: CommerceShareProvider): CommerceEventUnsubscribe {
        this.providerMap.set(provider.channel, provider);
        return () => {
            if (this.providerMap.get(provider.channel) === provider)
                this.providerMap.delete(provider.channel);
        };
    }

    public getProvider(channel: CommerceShareChannel): CommerceShareProvider | undefined {
        const provider = this.providerMap.get(channel);
        return provider && (provider.isAvailable?.() ?? true) ? provider : undefined;
    }

    public destinationUrl(
        resource: CommerceShareResource,
        channel: CommerceShareChannel,
    ): string | null {
        return (
            this.getProvider(channel)?.createUrl(resource) ?? resource.channelUrls[channel] ?? null
        );
    }

    public async copy(resource: CommerceShareResource): Promise<boolean> {
        if (typeof navigator === "undefined" || !navigator.clipboard) return false;
        try {
            await navigator.clipboard.writeText(resource.url);
            this.emit("copied", resource, "copy");
            return true;
        } catch {
            return false;
        }
    }

    public async native(resource: CommerceShareResource): Promise<boolean> {
        if (typeof navigator === "undefined" || !navigator.share) return false;
        await navigator.share({
            title: resource.title,
            text: resource.text,
            url: resource.url,
        });
        this.emit("native", resource, "native");
        return true;
    }

    public async resolve(
        shareId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceSharePreview> {
        this.assertAvailable();
        const preview = Object.freeze(await this.repository.resolve(shareId.trim(), options));
        this.emit("resolved", preview.resource);
        return preview;
    }

    public async accept(
        shareId: string,
        strategy: CommerceShareAcceptanceStrategy = "merge",
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCart> {
        this.assertAvailable();
        const result = await this.repository.accept({ shareId: shareId.trim(), strategy }, options);
        const cart = await this.acceptShare(result);
        this.emit("accepted", result.preview.resource);
        return cart;
    }

    public onEvent(listener: CommerceShareListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }
    public dispose(): void {
        this.listeners.release();
        this.providerMap.clear();
    }

    private emit(
        name: CommerceShareEvent["name"],
        resource: CommerceShareResource,
        channel?: CommerceShareChannel,
    ): void {
        this.listeners.emit(Object.freeze({ name, resource, ...(channel ? { channel } : {}) }));
    }
}

function createDefaultProviders(): CommerceShareProvider[] {
    const encoded = (resource: CommerceShareResource) =>
        encodeURIComponent(`${resource.text} ${resource.url}`);
    return [
        {
            channel: "email",
            label: "Email",
            priority: 30,
            createUrl: (resource) =>
                `mailto:?subject=${encodeURIComponent(resource.title)}&body=${encoded(resource)}`,
        },
        {
            channel: "sms",
            label: "SMS",
            priority: 40,
            createUrl: (resource) => `sms:?body=${encoded(resource)}`,
        },
        {
            channel: "whatsapp",
            label: "WhatsApp",
            priority: 50,
            createUrl: (resource) => `https://wa.me/?text=${encoded(resource)}`,
        },
        {
            channel: "facebook",
            label: "Facebook",
            priority: 60,
            createUrl: (resource) =>
                `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(resource.url)}`,
        },
        {
            channel: "messenger",
            label: "Messenger",
            priority: 70,
            createUrl: (resource) =>
                `fb-messenger://share/?link=${encodeURIComponent(resource.url)}`,
        },
        {
            channel: "x",
            label: "X",
            priority: 80,
            createUrl: (resource) =>
                `https://x.com/intent/post?text=${encodeURIComponent(resource.text)}&url=${encodeURIComponent(resource.url)}`,
        },
        {
            channel: "linkedin",
            label: "LinkedIn",
            priority: 90,
            createUrl: (resource) =>
                `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(resource.url)}`,
        },
        {
            channel: "pinterest",
            label: "Pinterest",
            priority: 100,
            createUrl: (resource) =>
                `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(resource.url)}&description=${encodeURIComponent(resource.text)}`,
        },
    ];
}
